import { mailMailboxAddress } from "@/services/api/mailboxApi";
import type { UserSettings } from "../useUserSettings";
import { SettingsSection } from "./SettingsSection";

/**
 * ROW 3 — "Connections" (D-206-010 row 3 — the MERGED row).
 *
 * **What was merged and why it is one section:** the earlier list carried *"who speaks for me"*
 * (mailboxes) and *"connected accounts"* (OAuth) separately. **A mailbox IS a connected account of
 * type mail**, so they are one section with two kinds of row. The header is `Connections` and NOT
 * *"Who speaks for me"* on purpose: LinkedIn and WhatsApp are not sending identities, so naming the
 * section after speaking would have been wrong on ACCURACY (D-206-010's rejected alternative).
 *
 * **Who does what:** the person connects the accounts Raisy works through; Raisy then sends from the
 * mailbox he chose and reads the calendar, the LinkedIn profile or the WhatsApp number he linked.
 * **What he sees:** each connection and its real state, with the identity that is connected.
 * **What he feels:** *"connect once, and it stays connected"* (FTUE implication 12) — and, when it is
 * not connected, exactly what is left to do rather than a grey ghost of a feature.
 *
 * WHY THIS ROW LINKS INSTEAD OF EDITING: connecting an account is an operation with a consent screen
 * and a provider round-trip, not a single value. The page states the state and hands over to the page
 * that owns the operation, so there is exactly ONE place in the product that runs an OAuth flow.
 *
 * ⚠️ A NOT-REPORTED CONNECTION IS NEVER RENDERED AS "NOT CONNECTED". The three status reads return
 * `connected: boolean`, but a read that FAILED is a different fact from a read that said `false` —
 * the first leaves the state unknown and is reported as such, because telling a person he has not
 * connected something he has connected is the most expensive lie this page could tell.
 */
export const ConnectionsSection = ({ settings }: { settings: UserSettings }) => {
  const { mailboxes, google, linkedin, whatsapp } = settings;

  type Connection = { name: string; state: string; tone: "on" | "off" | "unknown" };

  const mailRow: Connection =
    mailboxes.state === "loading"
      ? { name: "Sending mailboxes", state: "reading…", tone: "unknown" }
      : mailboxes.state === "unavailable"
        ? { name: "Sending mailboxes", state: "Not readable right now", tone: "unknown" }
        : mailboxes.value.length === 0
          ? { name: "Sending mailboxes", state: "none for this company yet", tone: "off" }
          : {
              name: "Sending mailboxes",
              // A `shared` mailbox belongs to the deployment, not to this company — the BE says so
              // and the row repeats it rather than letting the person believe it is his.
              state: mailboxes.value
                .map((m) => `${mailMailboxAddress(m)}${m.shared ? " (shared — not linked to a company yet)" : ""}`)
                .join(" · "),
              tone: "on",
            };

  const one = (
    name: string,
    read: { state: string; value?: { connected: boolean; email?: string | null; name?: string | null } ; reason?: string },
  ): Connection => {
    if (read.state === "loading") return { name, state: "reading…", tone: "unknown" };
    if (read.state === "unavailable") return { name, state: "Not readable right now", tone: "unknown" };
    if (!read.value?.connected) return { name, state: "not connected yet", tone: "off" };
    const identity = read.value.email ?? read.value.name ?? null;
    return { name, state: identity ? `connected as ${identity}` : "connected", tone: "on" };
  };

  const rows: Connection[] = [
    mailRow,
    one("Google — mail and calendar", google),
    one("LinkedIn", linkedin),
    one("WhatsApp", whatsapp),
  ];

  const connected = rows.filter((r) => r.tone === "on").length;
  const unknown = rows.filter((r) => r.tone === "unknown").length;

  return (
    <SettingsSection
      title="Connections"
      hint="The accounts Raisy works through for you — where it sends from, and what it can read."
      value={
        <span className="flex flex-col gap-1">
          <span>
            {connected === 0 ? "Nothing connected yet" : `${connected} of ${rows.length} connected`}
            {unknown > 0 && (
              <span className="ml-2 font-normal text-muted-foreground">
                {unknown === 1 ? "one could not be read" : `${unknown} could not be read`}
              </span>
            )}
          </span>
          <span className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-normal text-muted-foreground">
            {rows.map((r) => (
              <span key={r.name}>
                <span className="text-foreground">{r.name}</span>
                {" — "}
                <span className={r.tone === "on" ? "text-emerald-600" : undefined}>{r.state}</span>
              </span>
            ))}
          </span>
        </span>
      }
      link={{ to: "/integrations", label: "Open your connections" }}
    />
  );
};
